import { useRef, useState } from 'react'
import { Cog, QrCode, Search } from 'lucide-react'
import { ApiError, api } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { qrMissIsOtherPlant } from '@/lib/plantAccess'
import { machineGroups } from '@/lib/machinePicker'
import { Button, Input, Modal } from '@/components/ui'
import { QrScanner, tokenFromScan } from '@/components/QrScanner'
import { useT } from '@/i18n/I18nProvider'
import { cn } from '@/lib/utils'
import type { MyMachines, WorkCenter } from '@/types/api'

/**
 * The first question on the form: which machine.
 *
 * Three ways in, fastest first. A link or a sticker has usually answered it
 * already, and then this is one line with "Promijeni". Otherwise the person's
 * own machines are at the top of the list, and the search box takes a name or
 * a code typed with a thumb. "Skeniraj QR" is for the machine they are
 * standing next to and cannot name.
 *
 * The list is buttons rather than a `<select>`: a select shows one line until
 * it is opened, and the point of putting "your machines" first is that they
 * are visible without opening anything.
 */
export function MachinePicker({
  centres,
  hints,
  value,
  onChange,
  error,
  isOnline,
  loading,
}: {
  centres: WorkCenter[] | undefined
  hints: MyMachines | undefined
  value: string
  onChange: (id: string) => void
  error?: string
  isOnline: boolean
  loading: boolean
}) {
  const t = useT()
  const { user } = useAuth()
  const [search, setSearch] = useState('')
  const [scanning, setScanning] = useState(false)
  const [scanError, setScanError] = useState<string | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const selected = centres?.find((centre) => centre.id === value)
  const severalPlants = new Set((centres ?? []).map((centre) => centre.plantId)).size > 1

  async function resolveScan(text: string) {
    setScanning(false)

    if (!isOnline) {
      setScanError(t('submissions.form.machine.scanOffline'))
      return
    }

    try {
      const { data } = await api.get<{ data: WorkCenter }>(`/qr/${tokenFromScan(text)}`)
      setScanError(null)
      onChange(data.data.id)
    } catch (failure) {
      const status = failure instanceof ApiError ? failure.status : undefined
      setScanError(
        qrMissIsOtherPlant(status, user)
          ? t('tasks.qr.otherPlant')
          : t('submissions.form.machine.scanFailed'),
      )
    }
  }

  if (selected) {
    return (
      <div className="flex items-center gap-3 rounded-xl border bg-[var(--surface-sunken)] p-3">
        <span className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-xl">
          <Cog className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{selected.name}</p>
          <p className="muted truncate text-xs">
            {[selected.code, severalPlants ? selected.plant?.name : null].filter(Boolean).join(' · ')}
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            onChange('')
            // Straight into the search, so "change" is one tap and then typing.
            requestAnimationFrame(() => searchRef.current?.focus())
          }}
        >
          {t('submissions.form.machine.change')}
        </Button>
      </div>
    )
  }

  const groups = machineGroups(centres ?? [], hints, search)

  return (
    <div className="space-y-3">
      {value && centres && !loading && (
        <p role="alert" className="text-sm font-medium text-amber-700 dark:text-amber-400">
          {t('submissions.form.machine.unknown')}
        </p>
      )}

      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Search
            className="muted pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            ref={searchRef}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('submissions.form.machine.search')}
            aria-label={t('submissions.form.machine.search')}
            aria-invalid={error ? true : undefined}
            type="search"
            autoComplete="off"
            className="pl-9"
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setScanError(null)
            setScanning(true)
          }}
        >
          <QrCode className="size-4" aria-hidden />
          <span className="hidden sm:inline">{t('submissions.form.machine.scan')}</span>
          <span className="sr-only sm:hidden">{t('submissions.form.machine.scan')}</span>
        </Button>
      </div>

      {(error || scanError) && (
        <p role="alert" className="text-sm font-medium text-rose-600 dark:text-rose-400">
          {scanError ?? error}
        </p>
      )}

      {groups.length === 0 && !loading ? (
        <p className="muted text-sm">{t('submissions.form.machine.none')}</p>
      ) : (
        <div className="max-h-80 space-y-3 overflow-y-auto overscroll-contain">
          {groups.map((group) => (
            <section key={group.key} aria-label={group.plantName ?? t('submissions.form.machine.yours')}>
              {/* A heading only where there is something to tell apart. */}
              {(group.key === 'mine' || severalPlants || groups.length > 1) && (
                <h3 className="muted mb-1 text-xs font-semibold tracking-wide uppercase">
                  {group.key === 'mine' ? t('submissions.form.machine.yours') : group.plantName}
                </h3>
              )}
              <ul className="grid gap-1.5 sm:grid-cols-2">
                {group.machines.map((centre) => (
                  <li key={centre.id}>
                    <button
                      type="button"
                      onClick={() => onChange(centre.id)}
                      className={cn(
                        'min-h-control flex w-full items-center gap-2 rounded-xl border px-3 py-1.5 text-left text-sm transition-colors',
                        'hover:bg-muted/60 focus-visible:ring-ring/50 focus-visible:ring-3 focus-visible:outline-none',
                        group.key === 'mine' && 'border-primary/30 bg-primary/5',
                      )}
                    >
                      {/* One line per machine, so five of them fit where
                          three did: the name, and what tells two alike apart. */}
                      <span className="min-w-0 flex-1 truncate font-medium">{centre.name}</span>
                      <span className="muted max-w-[45%] shrink-0 truncate text-xs">
                        {group.key === 'mine' && severalPlants ? centre.plant?.name : centre.code}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <Modal
        open={scanning}
        onClose={() => setScanning(false)}
        title={t('submissions.form.machine.scanTitle')}
      >
        {/* Mounted only while open: the camera is released when it closes. */}
        {scanning && (
          <QrScanner
            onResult={(text) => void resolveScan(text)}
            onError={(message) => {
              setScanning(false)
              setScanError(message)
            }}
          />
        )}
      </Modal>
    </div>
  )
}
